import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AppShell from './components/AppShell';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Parts from './pages/Parts';
import RawMaterials from './pages/RawMaterials';
import Customers from './pages/Customers';
import Alerts from './pages/Alerts';
import Reports from './pages/Reports';
import Users from './pages/Users';
import Settings from './pages/Settings';
import RevisionCompare from './pages/RevisionCompare';
import ChangePasswordRequired from './pages/ChangePasswordRequired';
import Suppliers from './pages/Suppliers';
import SupplierDetail from './pages/SupplierDetail';
import PlatingComparo from './pages/PlatingComparo';
import MarginAlertSettings from './pages/MarginAlertSettings';
import Rbac from './pages/Rbac';
import { Toaster } from 'sonner';
import './App.css';

function ProtectedRoute({ children, allowedRoles = null, requiredCap = null }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-3 text-slate-500 text-sm font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (requiredCap && user.role !== 'admin' && !(user.capabilities || []).includes(requiredCap)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (user.must_change_password && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/change-password" element={
        <ProtectedRoute><ChangePasswordRequired /></ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={
        <ProtectedRoute><AppShell><Dashboard /></AppShell></ProtectedRoute>
      } />
      <Route path="/parts" element={
        <ProtectedRoute><AppShell><Parts /></AppShell></ProtectedRoute>
      } />
      <Route path="/materials" element={
        <ProtectedRoute><AppShell><RawMaterials /></AppShell></ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute><AppShell><Customers /></AppShell></ProtectedRoute>
      } />
      <Route path="/alerts" element={
        <ProtectedRoute><AppShell><Alerts /></AppShell></ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute><AppShell><Reports /></AppShell></ProtectedRoute>
      } />
      <Route path="/revisions" element={
        <ProtectedRoute><AppShell><RevisionCompare /></AppShell></ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['admin']}><AppShell><Users /></AppShell></ProtectedRoute>
      } />
      <Route path="/rbac" element={
        <ProtectedRoute allowedRoles={['admin']}><AppShell><Rbac /></AppShell></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={['admin']}><AppShell><Settings /></AppShell></ProtectedRoute>
      } />
      <Route path="/suppliers" element={
        <ProtectedRoute requiredCap="access_suppliers"><AppShell><Suppliers /></AppShell></ProtectedRoute>
      } />
      <Route path="/suppliers/:id" element={
        <ProtectedRoute requiredCap="access_suppliers"><AppShell><SupplierDetail /></AppShell></ProtectedRoute>
      } />
      <Route path="/plating" element={
        <ProtectedRoute requiredCap="access_plating"><AppShell><PlatingComparo /></AppShell></ProtectedRoute>
      } />
      <Route path="/margin-compare" element={<Navigate to="/parts?tab=margin-compare" replace />} />
      <Route path="/margin-alerts" element={
        <ProtectedRoute allowedRoles={['admin']}><AppShell><MarginAlertSettings /></AppShell></ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
